import {action, computed, observable} from 'mobx';
import {DomainStore} from 'shared/store';
import {api, t} from 'shared/core';
import {endpoints, types, formatTimeOffAmount} from 'main/tools';
import {dateToJson} from 'shared/tools';
import {ForecastedAccountBalance} from 'main/stores/time_off';
import DayOffViewModel from 'main/stores/time_off/DayOffViewModel';
import {AssignmentsList} from 'main/components';
import moment from 'moment';
import _ from 'lodash';

class TimeOffRequestDetailsState {
  static MAX_DAYS_OFF_ITEMS = 3;

  store = new DomainStore();

  @observable request;
  @observable existingBalance = {};
  @observable forecastedBalance = {};
  @observable daysOffJson = [];
  @observable expanded = false;

  @observable selectedNote = {};

  receiveProps({request}) {
    this.request = request;
  }

  @action async load() {
    await Promise.all([
      this.fetchRequestedDays(),
      this.forecastAccount()
    ]);
  }

  @action async fetchRequestedDays() {
    if (!this.request) return;

    const response = await api.get(
      endpoints.TIME_OFF.DAYS_OFF.FOR_REQUEST.with({
        employeeId: this.request.employee.id,
        id: this.request.id,
      })
    );

    this.daysOffJson = response.data;

    this.daysOffJson.forEach(day => {
      if (day.start_time) {
        day.start_time = moment(day.start_time).format(t('components.dates.TIMESTAMP_FORMAT'));
        day.end_time = moment(day.end_time).format(t('components.dates.TIMESTAMP_FORMAT'));
      }
    });
  }

  @action async forecastAccount() {
    if (!this.request) return;

    const {model, errors} = await this.store.post(
      endpoints.TIME_OFF.FORECAST_ACCOUNT_BALANCE.FOR_REQUEST.with({
        employeeId: this.request.employee.id,
        typeId: this.request.timeOffType.id,
        date: dateToJson(this.request.startDate),
        id: this.request.id
      }),
      types.TIME_OFF.REQUEST_FORECAST
    );
    this.errors = errors;

    if (model) {
      this.existingBalance = new ForecastedAccountBalance({
        ...model.balanceBefore,
        workDayLength: model.workDayLength,
        unlimited: model.unlimited,
        typeUnit: model.typeUnit
      });

      this.forecastedBalance = new ForecastedAccountBalance({
        ...model.balanceAfter,
        workDayLength: model.workDayLength,
        unlimited: model.unlimited,
        typeUnit: model.typeUnit
      });
    }
  }

  @computed get daysOff() {
    return this.daysOffJson.map(json => new DayOffViewModel(json, false));
  }

  @computed get totalCalendarDays() {
    return moment(this.request.endDate).diff(this.request.startDate, 'days') + 1;
  }

  @computed get totalNonWorkDays() {
    return _.filter(this.daysOff, 'hasCompanyDayOff').length;
  }

  @computed get includedHolidays() {
    return _.filter(this.daysOff, 'hasHoliday').map(d => d.holidayName);
  }

  @computed get summaryItems() {
    let items = [
      {
        title: t('employees.profile.time_off.request.summary.Duration'),
        value: t('employees.profile.time_off.request.summary.CALENDAR_DAYS', {days: this.totalCalendarDays})
      }
    ];
    if (this.totalNonWorkDays) {
      items.push({
        title: t('employees.profile.time_off.request.summary.Included Non-work Days'),
        value: t('employees.profile.time_off.request.summary.COST_IN_DAYS', {days: this.totalNonWorkDays})
      });
    }
    if (this.includedHolidays.length) {
      items.push({
        title: t('employees.profile.time_off.request.summary.Included Holidays'),
        value: this.includedHolidays.join(', ')
      });
    }
    items.push({
      title: t('employees.profile.time_off.request.summary.Total Cost'),
      value: formatTimeOffAmount(this.request.amount, this.request.timeOffType.unit)
    });

    if (this.request.status !== 'approved') {
      if (!_.isEmpty(this.request.firstStageStatus) && !_.isEmpty(this.request.secondStageStatus)) {
        const currentStage = this.request.firstStageStatus === 'approved' ? '2' : '1';
        items.push({
          title: t('time_off.requests.Current Stage'),
          value: t('time_off.requests.APPROVAL_STAGE', { currentStage })
        });
      }

      if (this.request.currentlyAssignedUsers.length > 0) {
        items.push({
          title: t('time_off.requests.Currently Assigned to'),
          value: <AssignmentsList assignments={this.request.currentAssignments} />
        });
      }
    }

    return items;
  }

  @computed get usageItems() {
    if (!this.forecastedBalance || !this.existingBalance) {
      return [];
    }

    return [
      {
        type: t('employees.profile.time_off.request.forecast.Available'),
        before: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.existingBalance.availableDaysOrHours, this.request.timeOffType.unit),
        after: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.forecastedBalance.availableDaysOrHours, this.request.timeOffType.unit),
        before_is_negative: !this.existingBalance.unlimited && this.existingBalance.availableDaysOrHours < 0,
        after_is_negative: !this.forecastedBalance.unlimited && this.forecastedBalance.availableDaysOrHours < 0
      },
      {
        type: t('employees.profile.time_off.request.forecast.Used'),
        before: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.existingBalance.usedDaysOrHours, this.request.timeOffType.unit),
        after: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.forecastedBalance.usedDaysOrHours, this.request.timeOffType.unit),
        before_is_negative: false,
        after_is_negative: false
      },
      {
        type: t('employees.profile.time_off.request.forecast.Scheduled'),
        before: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.existingBalance.scheduledDaysOrHours, this.request.timeOffType.unit),
        after: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Flexible') :
          formatTimeOffAmount(this.forecastedBalance.scheduledDaysOrHours, this.request.timeOffType.unit),
        before_is_negative: false,
        after_is_negative: false
      }
    ];
  }

  @computed get historyItems() {
    return this.request.actionHistory.map((action) => ({
      ...action,
      isMultiStage: !_.isEmpty(this.request.secondStageStatus)
    }));
  }

  @computed get expandedText() {
    return this.expanded ?
      t('time_off.requests.SHOW_LESS') :
      t('time_off.requests.SHOW_MORE', {
        num: this.daysOff.length - TimeOffRequestDetailsState.MAX_DAYS_OFF_ITEMS
      });
  }
}

export default TimeOffRequestDetailsState;
